// apps/web/src/pages/auth/MfaChallenge.tsx
//
// Plan 04-12b Task 2 — MFA challenge after password sign-in.
//
// FLOW (CONTEXT D-16 + Plan 04-08 default behavior):
//   - The user has just submitted password to /sign-in/email; Better-Auth's
//     twoFactor plugin saw user.twoFactorEnabled=true, DELETED the
//     just-set session cookie, and returned twoFactorRedirect:true. SignIn
//     redirected the user here.
//   - The user is NOT yet authenticated (no session cookie). The
//     intermediate two_factor cookie (set by Better-Auth) is what gates this
//     challenge.
//   - User enters TOTP code → POST /api/auth/two-factor/verify-totp
//     OR backup code → POST /api/auth/backup-codes/verify
//   - On success → session cookie is issued; redirect to /
//
// MODE TOGGLE: the MfaCodeField switches between TOTP and backup code
// formats via the Zod schema (MFA_CODE_PATTERNS).

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import {
  MFA_CODE_PATTERNS,
  MfaCodeField,
  type MfaCodeMode,
} from "@/components/forms/MfaCodeField";

const totpSchema = z.object({
  code: z.string().regex(MFA_CODE_PATTERNS.totp, "Enter a 6-digit code"),
});
const backupSchema = z.object({
  code: z
    .string()
    .regex(
      MFA_CODE_PATTERNS.backup,
      "Backup code must be 8 characters in the format XXXX-XXXX",
    ),
});

type ChallengeValues = { code: string };

export function MfaChallenge(): React.ReactElement {
  const navigate = useNavigate();
  const [mode, setMode] = useState<MfaCodeMode>("totp");
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<ChallengeValues>({
    resolver: zodResolver(mode === "totp" ? totpSchema : backupSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  async function onSubmit(values: ChallengeValues): Promise<void> {
    setServerError(null);
    try {
      const path =
        mode === "totp"
          ? "/api/auth/two-factor/verify-totp"
          : "/api/auth/backup-codes/verify";
      await api<{ ok: true }>(path, {
        method: "POST",
        body: JSON.stringify({ code: values.code }),
      });
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { error?: { code?: string; message?: string } };
        const body = err.body as ErrBody | null;
        const code = body?.error?.code;
        if (code === "RATE_LIMITED") {
          setServerError(
            "Too many failed attempts. Try your authenticator code or wait before retrying.",
          );
          return;
        }
        setServerError(body?.error?.message ?? "That code didn't match.");
        return;
      }
      setServerError(
        err instanceof Error ? err.message : "Could not verify the code",
      );
    }
  }

  function onToggleMode(next: MfaCodeMode): void {
    setMode(next);
    form.reset({ code: "" });
    setServerError(null);
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Two-factor verification</CardTitle>
          <CardDescription>
            {mode === "totp"
              ? "Enter the code from your authenticator app to finish signing in."
              : "Enter one of your saved backup codes to finish signing in."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              {serverError !== null ? (
                <Alert variant="destructive">
                  <AlertTitle>Verification failed</AlertTitle>
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              ) : null}
              <MfaCodeField
                control={form.control}
                name="code"
                mode={mode}
                onToggleMode={onToggleMode}
                disabled={form.formState.isSubmitting}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Verifying…" : "Verify"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/auth/sign-in" className="font-medium underline">
              Cancel and sign in again
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
